import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { FormControl } from '../ui/form';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { CalendarIcon } from 'lucide-react';
import { Calendar } from '../ui/calendar';
import { format } from 'date-fns';
import { DATE_FORMAT } from '@/constants/common';
import type { Matcher } from 'react-day-picker';
import type {
  ControllerRenderProps,
  FieldValues,
  FieldPath,
} from 'react-hook-form';

type DatePickerProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  field: ControllerRenderProps<TFieldValues, TName> & {
    value: Date | undefined;
    onChange: (date: Date | undefined) => void;
  };
  disabled?: boolean;
};

export type StandaloneDatePickerProps = {
  value?: Date;
  onChange: (date: Date | undefined) => void;
  disabled?: boolean;
  disabledDates?: Matcher | Matcher[];
  placeholder?: string;
  className?: string;
};

export const StandaloneDatePicker = ({
  value,
  onChange,
  disabled,
  disabledDates,
  placeholder = 'Pick a date',
  className,
}: StandaloneDatePickerProps) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            'w-full justify-between pl-3 text-left font-normal',
            !value && 'text-muted-foreground',
            className
          )}
          disabled={disabled}
        >
          {value ? format(value, DATE_FORMAT) : <span>{placeholder}</span>}
          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={disabled ? undefined : onChange}
          disabled={disabledDates}
          captionLayout="dropdown"
        />
      </PopoverContent>
    </Popover>
  );
};

const DatePicker = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  field,
  disabled,
}: DatePickerProps<TFieldValues, TName>) => {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <FormControl>
          <Button
            variant={'outline'}
            className={cn(
              'w-[240px] pl-3 text-left font-normal',
              !field.value && 'text-muted-foreground'
            )}
          >
            {field.value ? (
              format(field.value, DATE_FORMAT)
            ) : (
              <span>Pick a date</span>
            )}
            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
          </Button>
        </FormControl>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={field.value}
          onSelect={field.onChange}
          disabled={disabled}
          captionLayout="dropdown"
        />
      </PopoverContent>
    </Popover>
  );
};

export default DatePicker;
